import {
  ThemeSettingsValue,
  validateVars,
} from "components/themeSettingsModal/themeSettingsUtils";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { useDisplayThemeOverride } from "./useDisplayThemeOverride";

import { updateAttributesOnDocument } from "util/pouchActions";
import { useMeet } from "util/useMeet";
import { usePlatform } from "util/usePlatform";

export type StoredTableData = { k: string; w: number; v: number }[];

type Widget = {
  v?: boolean; // visible
  s?: number; // sizeMultiplier
  h?: number; // heightMultiplier
  x?: number; // x cords
  y?: number; // y cords
  mr?: number; // maxRecordsToShow
  md?: number; // maxDivisionsToShow
  ms?: number; // marqueeSpeed
  sd?: string; // standingsDivisionId
  sw?: string; // standingsWeightClassId
  sc?: number; // standingsCount
  tx?: string; // text
  is?: string; // imageSrc
  td?: StoredTableData; // Table Data
  theme?: ThemeSettingsValue; // Theme for display and remote pages only
};

export type WidgetId =
  | "b" // LifterName
  | "a" // AttemptInfo
  | "t" // TeamName
  | "d" // DivisionInfo
  | "r" // RecordInfo
  | "p" // PositionChange
  | "time" // Timer
  | "l" // Lights
  | "c" // LoadingChart
  | "o" // OrderTable
  | "n" // NextLoad
  | "no" // NextOverlay
  | "co" // CurrentOverlay
  | "m" // Marquee
  | "s" // Standings
  | "sb" // Scoreboard
  | "tx" // Free Text
  | "theme" // Theme for display and remote pages only
  | "i"; // Image

export type DisplayState = Record<WidgetId, Widget>;

export const defaultDisplayState: DisplayState = {
  b: { v: true, s: 1, x: 0, y: 0 },
  a: { v: true, s: 1, x: 0, y: 0 },
  t: { v: true, s: 1, x: 0, y: 0 },
  d: { v: true, s: 1, x: 0, y: 0, md: 1 },
  r: { v: true, s: 1, x: 0, y: 0, mr: 1 },
  p: { v: true, s: 1, x: 0, y: 0 },
  time: { v: true, s: 1, x: 0, y: 0 },
  l: { v: true, s: 1, x: 0, y: 0 },
  c: { v: true, s: 1, x: 0, y: 0 },
  o: { v: true, s: 1, h: 1, x: 0, y: 0, td: [] },
  n: { v: true, s: 1, x: 0, y: 0 },
  no: { v: false, s: 1, x: 0, y: 0 },
  co: { v: false, s: 1, x: 0, y: 0 },
  m: { v: false, s: 1, x: 0, y: 0, ms: 100 },
  s: { v: false, s: 1, x: 0, y: 0, sc: 10 },
  sb: { v: false, s: 1, h: 1, x: 0, y: 0, td: [] },
  tx: { v: false, s: 1, x: 0, y: 0, tx: "" },
  theme: {},
  i: { v: false, s: 1, x: 0, y: 0 },
};

export const getDisplayStateFromSearch = (search: string): DisplayState => {
  try {
    const searchParams = new URLSearchParams(search);

    const encodedData = searchParams.get("state");

    if (encodedData && typeof encodedData === "string") {
      const data = JSON.parse(atob(encodedData));

      return { ...defaultDisplayState, ...data };
    }
  } catch (e) {
    console.log("Failed to state from url", e);
  }

  return defaultDisplayState;
};

const normalizeDisplayTheme = (
  value: ThemeSettingsValue | undefined,
): ThemeSettingsValue | null => {
  if (!value) {
    return null;
  }

  const selectedTheme = value.theme;
  if (
    selectedTheme !== "light" &&
    selectedTheme !== "dark" &&
    selectedTheme !== "custom"
  ) {
    return null;
  }

  if (selectedTheme === "custom") {
    if (validateVars(value.customTheme)) {
      return null;
    }
  }

  return {
    theme: selectedTheme,
    customTheme: value.customTheme,
  };
};

const DisplaySettingsContext = createContext<
  | {
      editMode: boolean;
      state: DisplayState;
      updateComponent: (args: {
        componentId: WidgetId;
        newValues: Partial<Widget>;
      }) => void;
      resetToDefaults: () => void;
      reloadStateFromUrl: () => void;
      isTableSettingsModalOpen: boolean;
      setIsTableSettingsModalOpen: React.Dispatch<
        React.SetStateAction<boolean>
      >;
      updateRemoteScreenConfig: (platformId?: string) => void;
      isRemoteViewer: boolean;
    }
  | undefined
>(undefined);

export const DisplaySettingsProvider = ({
  children,
  isRemoteViewer,
  remoteScreenId,
  editMode,
  stateOverride,
}: {
  children: React.ReactNode;
  isRemoteViewer?: boolean;
  remoteScreenId?: string;
  editMode: boolean;
  stateOverride?: DisplayState;
}) => {
  const history = useHistory();

  const meet = useMeet();
  const platform = usePlatform();

  const [internalState, setState] = useState(defaultDisplayState);

  const state = stateOverride ?? internalState;
  const normalizedTheme = React.useMemo(() => {
    return normalizeDisplayTheme(state.theme.theme);
  }, [state.theme.theme]);

  useDisplayThemeOverride(normalizedTheme);

  const resetToDefaults = () => {
    setState(defaultDisplayState);
  };
  const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] =
    useState(false);
  const hasInitialized = useRef(false);

  const updateRemoteScreenConfig = React.useCallback(
    (platformId?: string) => {
      if (remoteScreenId && !isRemoteViewer) {
        updateAttributesOnDocument(meet._id, remoteScreenId, {
          search: window.location.search,
          platformId: platformId ?? platform._id,
        });
      }
    },
    [isRemoteViewer, meet._id, platform, remoteScreenId],
  );

  const loadSettingsFromUrl = React.useCallback(() => {
    setState(getDisplayStateFromSearch(window.location.search));
  }, []);

  useEffect(() => {
    if (!hasInitialized.current && !isRemoteViewer) {
      loadSettingsFromUrl();
      hasInitialized.current = true;
    }
  }, [isRemoteViewer, loadSettingsFromUrl]);

  const updateComponent = React.useCallback(
    ({
      componentId,
      newValues,
    }: {
      componentId: WidgetId;
      newValues: Partial<Widget>;
    }) => {
      const newState = {
        ...state,
        [componentId]: { ...state[componentId], ...newValues },
      };
      setState(newState);

      const encodedData = btoa(JSON.stringify(newState));

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("state", encodedData);

      history.replace({
        pathname: window.location.pathname,
        search: searchParams.toString(),
      });

      if (!isRemoteViewer && remoteScreenId) {
        updateRemoteScreenConfig();
      }
    },
    [history, isRemoteViewer, remoteScreenId, state, updateRemoteScreenConfig],
  );

  return (
    <DisplaySettingsContext.Provider
      value={{
        isRemoteViewer: !!isRemoteViewer,
        editMode: editMode,
        state,
        updateComponent,

        resetToDefaults,
        reloadStateFromUrl: loadSettingsFromUrl,
        isTableSettingsModalOpen,
        setIsTableSettingsModalOpen,
        updateRemoteScreenConfig,
      }}
    >
      {children}
    </DisplaySettingsContext.Provider>
  );
};

export const useDisplaySettings = () => {
  const context = useContext(DisplaySettingsContext);
  if (!context) {
    throw new Error(
      "useDisplaySettings must be used within a DisplaySettingsContext",
    );
  }
  return context;
};
